import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import Header from '../../components/ui/Header';
import PartnerContextHeader from '../../components/ui/PartnerContextHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import BasicProfileForm from './components/BasicProfileForm';
import DocumentUploadSection from './components/DocumentUploadSection';
import ContactsRolesPanel from './components/ContactsRolesPanel';
import ComplianceDueDiligencePanel from './components/ComplianceDueDiligencePanel';
import RegistrationStatusTracker from './components/RegistrationStatusTracker';
import { Helmet } from 'react-helmet';
import { partnerService, contactsService } from '../../services/partnerService';

const PartnerRegistrationDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic-profile');
  const [registrationStatus, setRegistrationStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [savedPartnerId, setSavedPartnerId] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  const [formData, setFormData] = useState({
    legalName: '',
    brandName: '',
    partnerType: '',
    country: '',
    website: '',
    yearEstablished: '',
    ownershipStructure: '',
    taxId: '',
    authorizedSignatory: ''
  });

  const [documents, setDocuments] = useState({});
  const [contacts, setContacts] = useState([]);
  const [compliance, setCompliance] = useState({
    ndaSigned: false,
    ndaSignedDate: new Date()?.toISOString(),
    agreementStatus: '',
    agreementDocument: '',
    sanctionsChecked: false,
    financialRiskLevel: ''
  });

  const [errors, setErrors] = useState({});

  const tabs = [
    { id: 'basic-profile', label: 'Basic Profile', icon: 'Building2' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'contacts', label: 'Contacts & Roles', icon: 'Users' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' }
  ];

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDocumentUpload = (docType, file) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: { file, uploadedAt: new Date()?.toISOString() }
    }));
  };

  const handleDocumentRemove = (docType) => {
    setDocuments(prev => {
      const updated = { ...prev };
      delete updated?.[docType];
      return updated;
    });
  };

  const handleContactAdd = (contact) => {
    setContacts(prev => [...prev, contact]);
  };

  const handleContactUpdate = (id, updatedContact) => {
    setContacts(prev => prev?.map(c => c?.id === id ? updatedContact : c));
  };

  const handleContactRemove = (id) => {
    setContacts(prev => prev?.filter(c => c?.id !== id));
  };

  const handleComplianceChange = (field, value) => {
    setCompliance(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.legalName?.trim()) newErrors.legalName = 'Legal name is required';
    if (!formData?.partnerType) newErrors.partnerType = 'Partner type is required';
    if (!formData?.country) newErrors.country = 'Country is required';
    if (!formData?.ownershipStructure) newErrors.ownershipStructure = 'Ownership structure is required';
    if (!formData?.taxId?.trim()) newErrors.taxId = 'Tax ID is required';
    if (!formData?.authorizedSignatory?.trim()) newErrors.authorizedSignatory = 'Authorized signatory is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const partnerPayload = {
        legal_name: formData?.legalName,
        brand_name: formData?.brandName,
        partner_type: formData?.partnerType || null,
        country: formData?.country,
        website: formData?.website,
        year_established: formData?.yearEstablished,
        ownership_structure: formData?.ownershipStructure,
        tax_id: formData?.taxId,
        authorized_signatory: formData?.authorizedSignatory,
        status: 'draft',
        nda_signed: compliance?.ndaSigned,
        sanctions_checked: compliance?.sanctionsChecked,
        financial_risk_level: compliance?.financialRiskLevel,
        agreement_status: compliance?.agreementStatus
      };

      let partner;
      if (savedPartnerId) {
        partner = await partnerService?.update(savedPartnerId, partnerPayload);
      } else {
        partner = await partnerService?.create(partnerPayload);
        setSavedPartnerId(partner?.id);
      }

      // Save contacts if any
      if (contacts?.length > 0 && partner?.id) {
        for (const contact of contacts) {
          if (!contact?._saved) {
            await contactsService?.create({
              partner_id: partner?.id,
              name: contact?.name,
              role: contact?.role,
              email: contact?.email,
              phone: contact?.phone,
              is_primary: contact?.isPrimary || false
            });
          }
        }
      }

      setSaveMessage('Draft saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveMessage(`Error saving: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!validateForm()) {
      setActiveTab('basic-profile');
      return;
    }
    if (Object.keys(documents)?.length < 3) {
      setActiveTab('documents');
      return;
    }
    if (contacts?.length === 0) {
      setActiveTab('contacts');
      return;
    }

    setSaving(true);
    try {
      await handleSaveDraft();
      if (savedPartnerId) {
        await partnerService?.updateStatus(savedPartnerId, 'submitted');
      }
      setRegistrationStatus('submitted');
      setSaveMessage('Registration submitted for review successfully!');
    } catch (err) {
      setSaveMessage(`Error submitting: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExportSummary = () => {
    const summary = {
      formData,
      documents: Object.keys(documents),
      contacts: contacts?.length,
      compliance,
      status: registrationStatus,
      exportedAt: new Date()?.toISOString()
    };
    console.log('Exporting summary:', summary);
  };

  const quickActions = [
    {
      icon: saving ? 'Loader' : 'Save',
      label: saving ? 'Saving...' : 'Save Draft',
      onClick: handleSaveDraft,
      variant: 'outline',
      primary: true,
      disabled: saving
    },
    {
      icon: 'Send',
      label: 'Submit for Review',
      onClick: handleSubmitForReview,
      variant: 'default',
      primary: true,
      disabled: registrationStatus !== 'draft' || saving
    },
    {
      icon: 'Download',
      label: 'Export Summary',
      onClick: handleExportSummary,
      variant: 'ghost'
    },
    {
      icon: 'FileText',
      label: 'View Guidelines',
      onClick: () => console.log('View guidelines'),
      variant: 'ghost'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Partner Registration Dashboard - PartnerHub</title>
        <meta name="description" content="Streamline partner onboarding with comprehensive registration, document management, and compliance tracking" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/20">
        <Header />
        <PartnerContextHeader />

        <main className="pt-8 pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">
            {/* Page Header */}
            <div className="mb-32">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20 mb-24">
                <div>
                  <div className="flex items-center gap-12 mb-12">
                    <div className="p-12 bg-gradient-to-br from-primary to-accent rounded-xl shadow-lg">
                      <Icon name="UserPlus" size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                        Partner Registration
                      </h1>
                      <p className="text-slate-600 mt-4">Complete onboarding and compliance verification</p>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-auto">
                  <QuickActionToolbar actions={quickActions} />
                </div>
              </div>

              {saveMessage && (
                <div className={`flex items-center gap-10 p-16 rounded-xl mb-20 text-sm ${
                  saveMessage?.includes('Error') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'
                }`}>
                  <Icon name={saveMessage?.includes('Error') ? 'AlertCircle' : 'CheckCircle2'} size={16} />
                  {saveMessage}
                </div>
              )}

              {/* Registration Status */}
              <div className="mb-24">
                <RegistrationStatusTracker 
                  status={registrationStatus}
                  currentStatus={registrationStatus}
                  onStatusChange={setRegistrationStatus}
                />
              </div>

              {/* Tabs */}
              <div className="flex gap-8 overflow-x-auto pb-4 scrollbar-hide">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-10 px-24 py-14 rounded-xl font-semibold text-sm whitespace-nowrap transition-all
                      ${
                        activeTab === tab?.id
                          ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/25'
                          : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={18} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-32">
              {activeTab === 'basic-profile' && (
                <BasicProfileForm formData={formData} errors={errors} onChange={handleFormChange} />
              )}
              {activeTab === 'documents' && (
                <DocumentUploadSection documents={documents} onUpload={handleDocumentUpload} onRemove={handleDocumentRemove} />
              )}
              {activeTab === 'contacts' && (
                <ContactsRolesPanel contacts={contacts} onAdd={handleContactAdd} onUpdate={handleContactUpdate} onRemove={handleContactRemove} />
              )}
              {activeTab === 'compliance' && (
                <ComplianceDueDiligencePanel compliance={compliance} onChange={handleComplianceChange} />
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PartnerRegistrationDashboard;